import { extendTheme, withDefaultColorScheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';
import borders from './foundations/borders';
import breakpoints from './foundations/breakpoints';
import defaultColors from './foundations/colors';
import { CompanyBrand } from '../models/companyBrand';
import { Skin, LedgerTone, ColorPref } from './SkinProvider';

const clamp = (n: number) => Math.min(255, Math.max(0, Math.round(n)));

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const rgbToHex = (r: number, g: number, b: number) => `#${[r, g, b]
  .map((c) => clamp(c).toString(16).padStart(2, '0'))
  .join('')}`;

/** mix a color toward white (weight 0..1) */
const tint = (hex: string, weight: number) => {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * weight, g + (255 - g) * weight, b + (255 - b) * weight);
};

/** mix a color toward black (weight 0..1) */
export const shade = (hex: string, weight: number) => {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - weight), g * (1 - weight), b * (1 - weight));
};

/**
 * Perceived-luminance check for contrast decisions: true → put white on it,
 * false → put black/dark on it. Invalid input counts as dark (white text is
 * the safe default on the stock Midnight Blue).
 */
export const isDarkColor = (hex?: string | null): boolean => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return true;
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6;
};

/** 0..1 colorfulness; near-white/near-black score ~0 (they can't glow) */
const vividness = (hex?: string | null): number => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return 0;
  const [r, g, b] = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (lum > 0.88 || lum < 0.08) return 0;
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
};

/**
 * The color that powers every glow, CTA gradient and active state: the most
 * VIVID of the two brand colors (a near-white accent can't glow — the skin
 * falls through to the primary, then to BasicSix green).
 */
export const glowColorFor = (
  brand?: { accentColor?: string | null; primaryColor?: string | null },
): string => {
  const accent = brand?.accentColor ?? null;
  const primary = brand?.primaryColor ?? null;
  const va = vividness(accent);
  const vp = vividness(primary);
  if (va < 0.12 && vp < 0.12) return '#59d140';
  return va >= vp ? (accent as string) : (primary as string);
};

/** build a Chakra 50–900 color scale around a base color (base = 500) */
export const makeScale = (hex: string) => ({
  50: tint(hex, 0.9),
  100: tint(hex, 0.75),
  200: tint(hex, 0.6),
  300: tint(hex, 0.4),
  400: tint(hex, 0.2),
  500: hex,
  600: shade(hex, 0.2),
  700: shade(hex, 0.4),
  800: shade(hex, 0.6),
  900: shade(hex, 0.8),
});

/**
 * The dark-glass design language (item 46, "Tesla" pass), generated from the
 * company accent: primary actions are accent gradients with a glow, secondary
 * actions are quiet glass. Buttons WITHOUT an explicit variant are CTAs.
 */
const makeButtonStyles = (accent: string) => {
  const deep = shade(accent, 0.4);
  const onAccent = isDarkColor(accent) ? 'white' : 'gray.900';
  return {
    baseStyle: {
      fontWeight: 'bold',
      letterSpacing: '0.06em',
      borderRadius: 'lg',
      // item 1183 (Chad's mobile pass, darts #6/#7) — a button NEVER shrinks below its
      // own label: flex rows squeezed buttons until the text spilled past the border.
      // The flexible neighbor (input/text) is always the right element to give way.
      flexShrink: 0,
    },
    variants: {
      // item 247c/248c — a clean, intentional CTA for ANY brand color (white-label):
      // a subtle gradient for depth, a soft ambient shadow at rest, and the glow grows
      // only on hover. Dropped the glossy white inset sheen + whiteAlpha border that
      // made a bright accent (e.g. lime) read as a glossy, off-brand gradient.
      solid: {
        bgGradient: `linear(135deg, ${accent}, ${deep})`,
        color: onAccent,
        border: 'none',
        boxShadow: `0 2px 10px ${accent}33`,
        transition: 'all 0.18s',
        _hover: {
          transform: 'translateY(-1px)',
          boxShadow: `0 6px 18px ${accent}55`,
          _disabled: { bgGradient: `linear(135deg, ${accent}, ${deep})` },
        },
        _active: { transform: 'translateY(0)', boxShadow: `0 1px 6px ${accent}33` },
      },
      outline: (props: Record<string, unknown>) => ({
        bg: 'transparent',
        borderColor: mode('gray.300', 'whiteAlpha.400')(props),
        color: mode('gray.800', 'whiteAlpha.900')(props),
        transition: 'all 0.2s',
        _hover: {
          bg: mode('blackAlpha.50', 'whiteAlpha.100')(props),
          boxShadow: `0 0 10px ${accent}40`,
          borderColor: accent,
        },
      }),
      ghost: (props: Record<string, unknown>) => ({
        bg: 'transparent',
        color: mode('gray.700', 'whiteAlpha.800')(props),
      }),
      // item 322b — THE destructive-action treatment (delete/void/wipe): quiet red
      // outline that fills on hover. One recipe instead of ad-hoc colorScheme="red"
      // + random variants per call site.
      danger: (props: Record<string, unknown>) => ({
        bg: 'transparent',
        borderWidth: '1px',
        borderColor: mode('red.300', 'red.400')(props),
        color: mode('red.600', 'red.300')(props),
        transition: 'all 0.2s',
        _hover: {
          bg: mode('red.50', 'rgba(254, 178, 178, 0.12)')(props),
          borderColor: 'red.400',
          boxShadow: '0 0 10px rgba(229, 62, 62, 0.35)',
        },
      }),
    },
    // bare <Button> = primary CTA (SAVE/FINISH/etc.); secondary = outline, tertiary =
    // ghost, destructive = danger — declare those explicitly
    defaultProps: { variant: 'solid' },
  };
};

/**
 * Glass overlays (item 46): modals (incl. the brand wizard) and menus float
 * as the same backdrop-blurred glass as the content cards.
 */
const glassOverlayStyles = {
  Modal: {
    baseStyle: (props: Record<string, unknown>) => ({
      overlay: {
        bg: 'blackAlpha.700',
        backdropFilter: 'var(--bx-glass-veil)',
      },
      dialog: {
        bg: mode('white', 'rgba(17, 21, 28, 0.85)')(props),
        backdropFilter: 'var(--bx-glass-panel)',
        borderWidth: '1px',
        borderColor: mode('gray.200', 'whiteAlpha.100')(props),
        borderRadius: 'xl',
        boxShadow: mode('lg', '0 16px 48px rgba(0, 0, 0, 0.6)')(props),
      },
      // item 732 (Chad) — density pass: Chakra's default modal header is xl / py-4 px-6 and the
      // body/footer are roomy. Tighten globally so every modal reads compact + consistent.
      header: {
        fontSize: 'md', fontWeight: 'semibold', px: 5, pt: 4, pb: 2,
      },
      body: { px: 5, py: 3 },
      footer: {
        px: 5, pt: 2, pb: 4,
      },
      closeButton: { top: 3, insetEnd: 3 },
    }),
  },
  Menu: {
    parts: ['list'],
    baseStyle: (props: Record<string, unknown>) => ({
      list: {
        // item 397 — near-opaque: 0.9 let page text bleed through the avatar menu
        bg: mode('white', 'rgba(13, 16, 22, 0.97)')(props),
        backdropFilter: 'var(--bx-glass-panel)',
        borderColor: mode('gray.200', 'whiteAlpha.100')(props),
        borderRadius: 'lg',
        boxShadow: mode('md', '0 12px 40px rgba(0, 0, 0, 0.55)')(props),
      },
      item: {
        bg: 'transparent',
        _hover: { bg: mode('blackAlpha.50', 'whiteAlpha.100')(props) },
        _focus: { bg: mode('blackAlpha.50', 'whiteAlpha.100')(props) },
      },
    }),
  },
};

// The Basix (Aurora) canvas tones — the base colour the ambient accent/primary glow
// floats over. Two were always here (bright Light + deep Dark); we add a Soft white
// and a Soft dark in between for lower glare. light/softLight are LIGHT-mode tones,
// softDark/dark are DARK-mode (AppearanceProvider pins Chakra's mode to match).
const BASIX_LIGHT = '#f6f7f9'; // bright porcelain
const BASIX_SOFT_LIGHT = '#e6e8ed'; // muted off-white, less glare
const BASIX_SOFT_DARK = '#161a23'; // soft slate, easier on the eyes than the deep dark
const BASIX_DARK = '#0b0e13'; // deep near-black

// Resolve the canvas a colour preference paints. A FIXED tone forces the SAME base into
// BOTH mode() slots (since AppearanceProvider has already pinned the matching Chakra
// mode, mode() returns it whichever way it resolves); only 'system' keeps the bright/
// deep pair so mode() can track the OS light/dark live.
const basixCanvas = (pref: ColorPref): { light: string; dark: string } => {
  switch (pref) {
    case 'light': return { light: BASIX_LIGHT, dark: BASIX_LIGHT };
    case 'softLight': return { light: BASIX_SOFT_LIGHT, dark: BASIX_SOFT_LIGHT };
    case 'softDark': return { light: BASIX_SOFT_DARK, dark: BASIX_SOFT_DARK };
    case 'dark': return { light: BASIX_DARK, dark: BASIX_DARK };
    default: return { light: BASIX_LIGHT, dark: BASIX_DARK }; // 'system' → track OS
  }
};

/**
 * The Sizzle-skin canvas (item 46): ambient accent/primary glows over a deep
 * near-black in dark mode; a whisper of the same glow on porcelain in light.
 * The base colour under each mode is the chosen Basix canvas tone (see basixCanvas).
 * Cards float as glass on top of this — see GlassCard.
 */
// item 1505 (Chad, iPhone screenshots) — iOS Safari auto-ZOOMS the page when a focused control's
// font-size is under 16px, and ~95 Input/Select sites run size="sm|xs" (14px/12px); after a tap
// the layout stayed zoomed and panned. Below md every native field reads at 16px — heights and
// padding stay on Chakra's size tokens, so nothing reflows — and the media scope leaves 1440/1920
// byte-identical. !important: Chakra's size recipes set font-size on the element's own class,
// which outranks a bare tag selector. Shared by BOTH skins' global styles.
const IOS_FOCUS_ZOOM_GUARD = {
  '@media screen and (max-width: 47.98em)': {
    'input, select, textarea': { fontSize: '16px !important' },
  },
};

const makeGlobalStyles = (
  accent: string,
  primary: string,
  lightBase: string,
  darkBase: string,
) => ({
  global: (props: Record<string, unknown>) => ({
    ...IOS_FOCUS_ZOOM_GUARD, // item 1505
    // item 322a — the ONE set of glass blur recipes. Every aurora surface reads
    // these vars (never a hardcoded blur), so the glass aesthetic is tuned here:
    //   panel = cards / grid panels / strips / form scaffolds / menus
    //   chip  = small floating elements (avatar chips, floating buttons)
    //   veil  = full-screen scrims behind modals / the PIN lock
    ':root': {
      '--bx-glass-panel': 'blur(20px) saturate(140%)',
      '--bx-glass-chip': 'blur(10px)',
      '--bx-glass-veil': 'blur(6px)',
      // item 455 — the app canvas color as a var, so full-screen layer panels can
      // paint an opaque surface that matches the body in the active mode/skin
      '--bx-app-canvas': mode(lightBase, darkBase)(props),
    },
    body: {
      color: mode('gray.800', 'whiteAlpha.900')(props),
      background: mode(lightBase, darkBase)(props),
    },
    // item 877 (Chad: "can't see the calendar icon") — native date/time controls draw their
    // picker indicator for a LIGHT page unless told otherwise; color-scheme makes the browser
    // render the indicator (and the native picker popup) for the active mode. Global, so every
    // raw <input type="date"> in the app (wizards, timesheet, Sales Bible…) is covered.
    'input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"]': {
      colorScheme: mode('light', 'dark')(props),
    },
    // item 1024 (Chad: "Same icon black issue for date") — Chrome ALSO paints each mm/dd/yyyy
    // segment (and the bare '—' of an empty value) with its own highlight box, which lands as
    // near-black/dark-red smudges on the dark canvas (Sales Bible board). Flatten the resting
    // segment chrome so the field reads as plain text; :focus keeps the browser's active-segment
    // highlight so in-place editing still shows where you are.
    ['input[type="date"]:not(:focus)::-webkit-datetime-edit-month-field, '
    + 'input[type="date"]:not(:focus)::-webkit-datetime-edit-day-field, '
    + 'input[type="date"]:not(:focus)::-webkit-datetime-edit-year-field, '
    + 'input[type="date"]:not(:focus)::-webkit-datetime-edit-text']: {
      background: 'transparent',
      color: 'inherit',
    },
    // item 330 — the ambient gradients live on a FIXED ::before layer instead of
    // `background-attachment: fixed`: same visual, but the layer is composited once
    // instead of repainting the full-viewport gradient on EVERY scroll frame (the
    // classic low-end-PC scroll killer, especially on Windows).
    'body::before': {
      content: '""',
      position: 'fixed',
      inset: 0,
      zIndex: -1,
      pointerEvents: 'none',
      background: mode(
        `radial-gradient(1100px 520px at 85% -10%, ${accent}12, transparent 60%)`,
        `radial-gradient(1100px 520px at 85% -10%, ${accent}1f, transparent 60%), `
        + `radial-gradient(900px 480px at -10% 110%, ${primary}38, transparent 60%)`,
      )(props),
    },
    '::selection': {
      background: `${accent}55`,
    },
  }),
});

// ──────────────────────────────────────────────────────────────────────────
// Accountant LEDGER skin (item 61, aggressive pass): "an accounting ledger on a
// typewriter." Courier Prime monospace everywhere, off-cream paper (warm charcoal
// in dark mode), hard 1px ink strokes, square corners and FULLY MONOCHROME — the
// company brand accent is suppressed (no green, no glow, no gradient). This is a
// self-contained theme; it ignores the brand colours entirely (the logo still
// shows — that's an <Image>, not the theme).

const MONO = "'Courier Prime', 'Courier New', Courier, monospace";

interface LedgerPalette {
  mode: 'light' | 'dark';
  paper: string; // the page
  surface: string; // cards, menus, modals
  surfaceAlt: string; // hover / striped row
  ink: string; // text + strokes
  stroke: string; // ruled lines / borders
  muted: string; // secondary text
}

// The four typed papers (item 61). brilliant/soft are LIGHT tones, softDark/dark are
// DARK tones — each is a COMPLETE fixed palette (no colour-mode inversion: the tone
// already picks the mode). The platen never changes ink mid-page.
export const LEDGER_TONES: Record<LedgerTone, LedgerPalette> = {
  brilliant: {
    mode: 'light',
    paper: '#ffffff',
    surface: '#ffffff',
    surfaceAlt: '#eeece6',
    ink: '#141109',
    stroke: '#141109',
    muted: '#5c574a',
  },
  soft: {
    mode: 'light',
    paper: '#efe9da',
    surface: '#f7f2e6',
    surfaceAlt: '#e9e1cd',
    ink: '#1c1a12',
    stroke: '#1c1a12',
    muted: '#6b6452',
  },
  softDark: {
    mode: 'dark',
    paper: '#2a2620',
    surface: '#322d25',
    surfaceAlt: '#3b362c',
    ink: '#efe9da',
    stroke: '#cfc8b2',
    muted: '#a39d88',
  },
  dark: {
    mode: 'dark',
    paper: '#14120c',
    surface: '#1d1a12',
    surfaceAlt: '#262118',
    ink: '#ece6d5',
    stroke: '#cfc8b2',
    muted: '#9a9379',
  },
};

// FLAT colour tokens so the flat ('!aurora') branch of shared surfaces can paint
// ledger paper/ink/stroke by name (bg="ledgerSurface", borderColor="ledgerStroke").
// NOTE: this Chakra (1.6.7) predates `semanticTokens`, so these MUST live in
// `colors` as plain string tokens — a semanticTokens entry would silently resolve to
// nothing (transparent). The tone is fixed per-build, so no light/dark variants are
// needed; each Accountant theme bakes one paper.
const ledgerColors = (t: LedgerPalette) => ({
  ledgerPaper: t.paper,
  ledgerSurface: t.surface,
  ledgerSurfaceAlt: t.surfaceAlt,
  ledgerInk: t.ink,
  ledgerStroke: t.stroke,
  ledgerMuted: t.muted,
});

// a warm, blue-free replacement for Chakra's slate `gray` scale (used app-wide for
// borders/surfaces/text) so the Accountant ledger never tints navy.
const WARM_GRAY = {
  50: '#f7f5f0',
  100: '#e9e5db',
  200: '#d6d0c2',
  300: '#b8b1a0',
  400: '#938c7a',
  500: '#6f6857',
  600: '#544e40',
  700: '#3a352a',
  800: '#262219',
  900: '#17140d',
};

// Every palette the app paints chrome/actions/inputs from — brand, brandGreen,
// brandBlue and Chakra's default blue — is rebuilt as a monochrome ramp anchored on
// the chosen paper, so NOTHING leaks the stock midnight-blue or green into the
// ledger (brand.700 labels, brand.900 chrome, blue.500 focus rings all go ink).
// The ramp stays readable on both light and dark papers: secondary roles map to the
// tone's `muted`, strong roles to its `ink`.
const monoPalettes = (t: LedgerPalette) => {
  const ramp = {
    50: t.surfaceAlt,
    100: t.surfaceAlt,
    200: t.stroke,
    300: t.muted,
    400: t.muted,
    500: t.ink,
    600: t.ink,
    700: t.muted, // form labels (brand.700) — readable secondary ink
    800: t.ink,
    900: t.ink,
  };
  return {
    brand: {
      ...ramp,
      901: `${t.ink}a6`,
      900: t.ink, // deep primary chrome (was midnight blue)
      100: t.ink, // accent key (was green)
      50: t.surfaceAlt, // subtle focus-border / selected bg
      20: t.surfaceAlt,
      11: `${t.surface}a6`,
      10: t.surface,
      0: t.paper,
    },
    brandGreen: ramp,
    brandBlue: ramp,
    blue: ramp,
    // Chakra's stock `gray` is a BLUE-tinted slate (gray.800 = #1A202C) — any
    // `gray.700/800/900` left in a flat surface paints navy in dark Accountant. Swap
    // in a warm neutral so nothing reads blue, whatever component forgot to tokenise.
    gray: WARM_GRAY,
  };
};

// buttons read like a rubber STAMP: paper fill + hard ink stroke, inverting to
// solid ink on hover. No gradient, no glow, no lift.
const makeLedgerButton = (t: LedgerPalette) => ({
  baseStyle: {
    flexShrink: 0, // item 1183 — buttons never shrink below their label
    fontFamily: MONO,
    fontWeight: 'bold',
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    borderRadius: '2px',
  },
  variants: {
    solid: {
      bg: t.surface,
      color: t.ink,
      borderWidth: '1px',
      borderColor: t.stroke,
      boxShadow: 'none',
      transition: 'none',
      _hover: { bg: t.ink, color: t.paper, _disabled: { bg: t.surface } },
      _active: { bg: t.ink, color: t.paper },
    },
    outline: {
      bg: 'transparent',
      color: t.ink,
      borderWidth: '1px',
      borderColor: t.stroke,
      _hover: { bg: t.ink, color: t.paper },
    },
    ghost: {
      bg: 'transparent',
      color: t.ink,
      _hover: { bg: t.surfaceAlt },
    },
    // item 322b — destructive stamp: same hard-stroke language, red ink. The ledger
    // skin is monochrome by design, but destructive actions earn the one exception.
    danger: {
      bg: 'transparent',
      color: '#c53030',
      borderWidth: '1px',
      borderColor: '#c53030',
      _hover: { bg: '#c53030', color: t.paper },
    },
  },
  defaultProps: { variant: 'solid' },
});

// modals + menus are flat paper with a hard ink stroke — no glass, no blur, no shadow
const makeLedgerOverlayStyles = (t: LedgerPalette) => ({
  Modal: {
    baseStyle: {
      overlay: { bg: 'blackAlpha.600', backdropFilter: 'none' },
      dialog: {
        bg: t.surface,
        color: t.ink,
        borderWidth: '1px',
        borderColor: t.stroke,
        borderRadius: '2px',
        boxShadow: 'none',
      },
      // item 732 (Chad) — density pass: match the compact modal header/padding used in Aurora
      header: {
        fontSize: 'md', fontWeight: 'semibold', px: 5, pt: 4, pb: 2,
      },
      body: { px: 5, py: 3 },
      footer: {
        px: 5, pt: 2, pb: 4,
      },
      closeButton: { top: 3, insetEnd: 3 },
    },
  },
  Menu: {
    parts: ['list', 'item'],
    baseStyle: {
      list: {
        bg: t.surface,
        color: t.ink,
        borderColor: t.stroke,
        borderRadius: '2px',
        boxShadow: 'none',
        backdropFilter: 'none',
      },
      item: {
        bg: 'transparent',
        _hover: { bg: t.surfaceAlt },
        _focus: { bg: t.surfaceAlt },
      },
    },
  },
});

const makeLedgerGlobalStyles = (t: LedgerPalette) => ({
  global: {
    ...IOS_FOCUS_ZOOM_GUARD, // item 1505
    // item 322a — glass vars exist in BOTH skins so unconditional consumers (e.g.
    // the CommandPalette overlay) never hit an undefined var; ledger flattens them.
    ':root': {
      '--bx-glass-panel': 'none',
      '--bx-glass-chip': 'none',
      '--bx-glass-veil': 'blur(2px)',
      '--bx-app-canvas': t.paper, // item 455 — layer panels sit on flat ledger paper
    },
    // item 877 — native date/time picker indicators follow the paper's mode (see the aurora twin)
    'input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"]': {
      colorScheme: t.mode,
    },
    body: {
      color: t.ink,
      // solid paper — no fixed attachment needed (item 330: it forced full-viewport
      // repaints on every scroll frame for no visual gain on a flat color)
      background: t.paper,
      fontFamily: MONO,
    },
    '::selection': { background: `${t.ink}26` },
  },
});

const buildAccountantTheme = (tone: LedgerTone) => {
  const t = LEDGER_TONES[tone] ?? LEDGER_TONES.soft;
  return extendTheme(
    {
      // initialColorMode MUST stay constant across every paper (and match the Basix
      // theme): Chakra's ColorModeProvider re-runs its init effect whenever this
      // value changes and resets the live mode to the stale document-root value,
      // clobbering AppearanceProvider's setColorMode (the "needs two toggles" bug).
      // The real light/dark for each paper is applied by AppearanceProvider, not here.
      config: { initialColorMode: 'dark', useSystemColorMode: false },
      fonts: { heading: MONO, body: MONO, mono: MONO },
      // item 406 — Chakra 1.6.7 has no 2xs token; chat micro-labels use it
      fontSizes: { '2xs': '0.625rem' },
      breakpoints,
      styles: makeLedgerGlobalStyles(t),
      colors: {
        ...defaultColors,
        ...monoPalettes(t),
        ...ledgerColors(t),
        // solid paper backing for the sticky header stack so scrolled content never
        // bleeds through it (pages stay sticky and never overlap)
        appHeaderBacking: t.paper,
      },
      borders,
      // square the corners app-wide for the ruled-ledger feel (pills keep `full`)
      radii: {
        sm: '2px', md: '2px', lg: '2px', xl: '2px', '2xl': '2px',
      },
      shadows: { outline: `0 0 0 2px ${t.muted}` },
      components: { Button: makeLedgerButton(t), ...makeLedgerOverlayStyles(t) },
    },
    withDefaultColorScheme({ colorScheme: 'brandGreen' }),
  );
};

/**
 * Build the runtime Chakra theme from the company brand package (item 35).
 * With no brand (still loading), this returns the stock Basix theme.
 *
 * The Accountant skin (item 61) short-circuits to a self-contained monochrome
 * typewriter-ledger theme and ignores the brand colours entirely.
 *
 * The app's components key off the `brand` (primary/chrome) and `brandGreen`
 * (action/colorScheme) palettes, so those are regenerated from the company's
 * primary and accent colors; the custom `brand` keys (901, 20, 11, 10, 0)
 * keep their roles (alpha/gray/white helpers).
 */
const buildBrandTheme = (
  brand?: CompanyBrand,
  skin: Skin = 'basix',
  ledgerTone: LedgerTone = 'soft',
  colorPref: ColorPref = 'system',
) => {
  if (skin === 'accountant') return buildAccountantTheme(ledgerTone);

  const fonts = brand
    ? {
      heading: `'${brand.fontFamily}', sans-serif`,
      body: `'${brand.fontFamily}', sans-serif`,
    }
    : { heading: 'Montserrat', body: 'Montserrat' };

  const colors = brand
    ? {
      ...defaultColors,
      brand: {
        ...defaultColors.brand,
        901: `${brand.primaryColor}a6`,
        900: brand.primaryColor,
        100: brand.accentColor,
        50: tint(brand.accentColor, 0.45),
      },
      // the ACTION palette keys off the auto-picked glow color, not the raw
      // accent — a near-white accent would wash out every control
      brandGreen: makeScale(glowColorFor(brand)),
      brandBlue: makeScale(brand.primaryColor),
      ...(brand.successColor ? { green: makeScale(brand.successColor) } : {}),
      ...(brand.warningColor
        ? { orange: makeScale(brand.warningColor), warning: { 400: brand.warningColor } }
        : {}),
      ...(brand.errorColor ? { red: makeScale(brand.errorColor) } : {}),
    }
    : defaultColors;

  const glow = glowColorFor(brand);
  const primary = brand?.primaryColor ?? '#002d45';
  const canvas = basixCanvas(colorPref);

  return extendTheme(
    {
      // the skin is designed dark-first; the moon toggle still works
      config: { initialColorMode: 'dark', useSystemColorMode: false },
      fonts,
      // item 406 — Chakra 1.6.7 has no 2xs token; chat micro-labels use it
      fontSizes: { '2xs': '0.625rem' },
      breakpoints,
      styles: makeGlobalStyles(glow, primary, canvas.light, canvas.dark),
      // the Aurora header stack stays frosted/transparent by design
      colors: { ...colors, appHeaderBacking: 'transparent' },
      borders,
      // glow-color focus ring instead of Chakra's default blue
      shadows: { outline: `0 0 0 3px ${glow}66` },
      components: { Button: makeButtonStyles(glow), ...glassOverlayStyles },
    },
    withDefaultColorScheme({ colorScheme: 'brandGreen' }),
  );
};

export default buildBrandTheme;
